import { Component, OnInit, ViewEncapsulation } from '@angular/core'
import { FormBuilder, FormGroup, Validators } from '@angular/forms'
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap'
import { HttpErrorResponse } from '@angular/common/http'
import { ToastrService } from 'ngx-toastr'
import { AuthenticationService } from '../../../../modules/auth/core/services/authentication.service'
import { ITwoFactorStatusResponse } from '../../../../modules/auth/core/interfaces/ITwoFactorStatusResponse'

type ModalStep = 'status' | 'clients-code' | 'regen-code' | 'regen-backup'

interface ClientOption {
    key: string
    label: string
    mandatory: boolean
    enabled: boolean
}

@Component({
    selector: 'app-2fa-settings-modal',
    templateUrl: './2fa-settings-modal.component.html',
    encapsulation: ViewEncapsulation.None,
})
export class TwoFactorSettingsModalComponent implements OnInit {
    protected step: ModalStep = 'status'
    protected isLoading: boolean = false
    protected codeForm: FormGroup
    protected status: ITwoFactorStatusResponse | null = null
    protected clientOptions: ClientOption[] = []
    protected backupCodes: string[] = []
    protected copied: boolean = false
    protected challengeSent: boolean = false
    protected maskedEmail: string = ''

    private _originalClients: string[] = []

    get hasChanges(): boolean {
        if (!this.status) {
            return false
        }
        const current: string[] = this.clientOptions.filter(c => c.enabled).map(c => c.key)
        const original: string[] = [...this._originalClients].sort()
        return JSON.stringify([...current].sort()) !== JSON.stringify(original)
    }

    get methodLabel(): string {
        if (!this.status?.method) {
            return ''
        }
        return this.status.method === 'totp' ? 'TOTP (приложение)' : 'Email'
    }

    constructor(
        public activeModal: NgbActiveModal,
        private _formBuilder: FormBuilder,
        private _toastrService: ToastrService,
        private _authenticationService: AuthenticationService,
    ) {
        this.codeForm = this._formBuilder.group({
            code: [null, [Validators.required, Validators.minLength(6), Validators.maxLength(8)]],
        })
    }

    ngOnInit(): void {
        this._loadStatus()
    }

    private _loadStatus(): void {
        this.isLoading = true
        this._authenticationService.get2faStatus().subscribe(
            (status: ITwoFactorStatusResponse): void => {
                this._applyStatus(status)
                this.isLoading = false
            },
            (error: HttpErrorResponse): void => {
                this._toastrService.error(error?.error?.message || 'Ошибка загрузки статуса 2FA')
                this.isLoading = false
            },
        )
    }

    private _applyStatus(status: ITwoFactorStatusResponse): void {
        this.status = status
        this.clientOptions = [
            {
                key: 'admin',
                label: 'Панель администратора',
                mandatory: status.mandatory_clients.includes('admin'),
                enabled: status.mandatory_clients.includes('admin') || status.clients.includes('admin'),
            },
            {
                key: 'web',
                label: 'Веб-приложение',
                mandatory: status.mandatory_clients.includes('web'),
                enabled: status.mandatory_clients.includes('web') || status.clients.includes('web'),
            },
            {
                key: 'mobile',
                label: 'Мобильное приложение',
                mandatory: status.mandatory_clients.includes('mobile'),
                enabled: status.mandatory_clients.includes('mobile') || status.clients.includes('mobile'),
            },
        ]
        this._originalClients = this.clientOptions.filter(c => c.enabled).map(c => c.key).sort()
    }

    protected toggleClient(option: ClientOption): void {
        if (option.mandatory) {
            return
        }
        option.enabled = !option.enabled
    }

    protected openClientsCode(): void {
        this.codeForm.reset()
        this.challengeSent = false
        if (this.status?.method === 'email') {
            this._challengeEmail('manage_clients')
        }
        this.step = 'clients-code'
    }

    protected openRegenCode(): void {
        this.codeForm.reset()
        this.challengeSent = false
        if (this.status?.method === 'email') {
            this._challengeEmail('regenerate_backup_codes')
        }
        this.step = 'regen-code'
    }

    private _challengeEmail(action: 'manage_clients' | 'regenerate_backup_codes'): void {
        this._authenticationService.challenge2fa(action).subscribe(
            (response: { sent: boolean; masked_email: string }): void => {
                this.challengeSent = response.sent
                this.maskedEmail = response.masked_email
            },
            (error: HttpErrorResponse): void => {
                this._toastrService.error(error?.error?.message || 'Ошибка отправки кода')
            },
        )
    }

    protected submitClientsCode(): void {
        this.isLoading = true
        const clients: string[] = this.clientOptions.filter(c => c.enabled).map(c => c.key)
        this._authenticationService.updateClients(clients, this.codeForm.value.code).subscribe(
            (status: ITwoFactorStatusResponse): void => {
                this._applyStatus(status)
                this.step = 'status'
                this.isLoading = false
                this._toastrService.success('Настройки клиентов 2FA обновлены')
            },
            (error: HttpErrorResponse): void => {
                this._toastrService.error(error?.error?.message || 'Неверный код')
                this.isLoading = false
            },
        )
    }

    protected submitRegenCode(): void {
        this.isLoading = true
        this._authenticationService.regenerateBackupCodes(this.codeForm.value.code).subscribe(
            (response: { backup_codes: string[] }): void => {
                this.backupCodes = response.backup_codes
                this.step = 'regen-backup'
                this.isLoading = false
            },
            (error: HttpErrorResponse): void => {
                this._toastrService.error(error?.error?.message || 'Неверный код')
                this.isLoading = false
            },
        )
    }

    protected copyBackupCodes(): void {
        const text: string = this.backupCodes.join('\n')
        navigator.clipboard.writeText(text).then((): void => {
            this.copied = true
            setTimeout((): void => {
                this.copied = false
            }, 2000)
        })
    }

    protected goToStatus(): void {
        this.step = 'status'
    }
}
